import {
	ATTRIBUTES,
	ON_CHANGE_DEBOUNCE_TIMER,
	PUB_SUB_EVENTS,
	SELECTORS,
} from '@/scripts/core/global'
import { fetchConfig } from '@/scripts/core/global'
import { TsDOM as q, debounce } from '@/scripts/core/TsDOM'
import { publish, PubSubEvent, subscribe } from '@/scripts/core/global'
import { type ShopifySectionRenderingSchema } from '@/scripts/types/theme'
import { type CartDrawer } from '@/scripts/cart/cart-drawer'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
	fetchCartWithSections,
	hasRequiredCartSections,
	showCartErrorNotification,
} from '@/scripts/core/cart-functions'
import {
	beginStandardCartMutation,
	rejectStandardCartMutation,
	resolveStandardCartMutation,
} from '@/scripts/platform/standard-events'
import type { Cart } from '@/scripts/shopify'

export class CartItems extends UcoastEl {
	// static
	static htmlSelector = 'cart-items'
	static selectors = {
		element: 'cart-items',
		itemLink: '[data-uc-cart-item-name]',
		lineItemStatus: '[data-uc-cart-status]',
		errors: '[data-uc-cart-errors]',
		liveRegionText: '#CartPage-LiveRegionText',
		main: '#main-cart-items',
		cartDrawerInner: '[data-uc-cart-drawer-inner]',
		item: '[data-uc-cart-item]',
		itemErrorText: '[data-uc-cart-item-error-text]',
		footer: '#CartPage-Footer',
		loadingOverlay: '[data-uc-loading-overlay]',
		// the following selectors are partial - they will be concatenated with the line ID
		line: '#CartPage-Item', // ex: `${this.instanceSelectors.line}-${line}`
		lineQuantity: '#CartPage-LineItemQuantity', // ex: `${this.instanceSelectors.lineQuantity}-${line}`
		lineError: '#CartPage-LineItemError', // ex: `${this.instanceSelectors.lineError}-${line}`
	}
	// TODO: refactor to global attributes selector
	static attributes = {
		disabled: 'data-uc-disabled',
	}

	// instance specific property types
	instanceSelectors = CartItems.selectors
	lineItemStatusElement: HTMLElement
	cartUpdateUnsubscriber?: () => void = undefined

	// init
	constructor() {
		super()
		this.setInstanceSelectors()
		this.lineItemStatusElement = q.rs(this.instanceSelectors.lineItemStatus)

		const debouncedOnChange = debounce((event: Event) => {
			this.onChange(event)
		}, ON_CHANGE_DEBOUNCE_TIMER)

		this.addEventListener('change', debouncedOnChange.bind(this))
	}

	// have to instantiate selectors here so they can be different for CartDrawerItems
	setInstanceSelectors() {
		this.instanceSelectors = CartItems.selectors
	}

	// from here on is default Dawn CartItems -> typescript
	override connectedCallback() {
		this.cartUpdateUnsubscriber = subscribe(
			PUB_SUB_EVENTS.cartUpdate,
			(pubSubEvent: PubSubEvent) => {
				if (
					pubSubEvent &&
					'source' in pubSubEvent &&
					(pubSubEvent.source === 'cart-items' ||
						(this.isDrawerCart() && pubSubEvent.source === 'product-form'))
				) {
					return
				}
				this.onCartUpdate()
			}
		)
	}

	override disconnectedCallback() {
		if (this.cartUpdateUnsubscriber) {
			this.cartUpdateUnsubscriber()
		}
	}

	isDrawerCart() {
		return this.localName === 'cart-drawer-items'
	}

	showCartError(message = window.cartStrings.error) {
		if (showCartErrorNotification(message)) return true
		if (!this.isDrawerCart()) return false
		const cartDrawer = q.os<CartDrawer>('cart-drawer')
		if (!cartDrawer) return false
		cartDrawer.showError(message)
		return true
	}

	onChange(event: Event) {
		const target = q.rt<Event, HTMLInputElement>(event)
		const targetIndex = target.dataset.index
		if (!targetIndex) return
		this.updateQuantity(
			targetIndex,
			target.value,
			target.name || undefined
		)
	}

	onCartUpdate() {
		fetch(`${window.routes.cart_url}?section_id=main-cart-items`)
			.then((response) => response.text())
			.then((responseText) => {
				const html = new DOMParser().parseFromString(
					responseText,
					'text/html'
				)
				// reference CartItems selectors here since this returns then inner HTML I guess
				const source = q.rs(
					CartItems.selectors.element,
					html.documentElement
				)
				this.innerHTML = source.innerHTML
			})
			.catch((e) => {
				console.error(e)
			})
	}

	getSectionsToRender(): ShopifySectionRenderingSchema[] {
		return [
			{
				id: 'main-cart-items',
				section: q.ra(q.rs(this.instanceSelectors.main), 'data-id'),
				selector: '.js-contents',
			},
			{
				id: 'CartIconBubble',
				section: 'cart-icon-bubble',
				selector: SELECTORS.cartLink,
			},
			{
				id: 'CartPage-LiveRegionText',
				section: 'cart-live-region-text',
				selector: '.shopify-section',
			},
			{
				id: 'CartPage-Footer',
				section: q.ra(q.rs(this.instanceSelectors.footer), 'data-id'),
				selector: '.js-contents',
			},
		]
	}

	updateQuantity(
		line: string,
		quantity: string,
		name?: string,
		sellingPlanId?: string | null
	) {
		this.enableLoading(line)
		const requestedQuantity = parseInt(quantity)
		const lineElement = q.os<HTMLElement>(
			`${this.instanceSelectors.line}-${line}`,
			this
		)
		const lineId = lineElement?.dataset.standardCartLineId ?? line
		const standardBoundary = beginStandardCartMutation(this, 'cart', [
			{
				action: requestedQuantity === 0 ? 'remove' : 'update',
				id: lineId,
				quantity: requestedQuantity,
			},
		])

		const change: {
			line: string
			quantity: string
			selling_plan?: string | null
			sections: Array<string | undefined>
			sections_url: string
		} = {
			line,
			quantity,
			sections: this.getSectionsToRender().map(
				(section) => section.section
			),
			sections_url: window.location.pathname,
		}

		if (sellingPlanId !== undefined) {
			change.selling_plan = sellingPlanId === '' ? null : sellingPlanId
		}

		const body = JSON.stringify(change)

		fetch(`${window.routes.cart_change_url}`, {
			...fetchConfig(),
			...{ body },
		})
			.then((response) => {
				return response.text()
			})
			.then(async (state) => {
				let parsedState = JSON.parse(state)
				const quantityElement = q.rs<HTMLInputElement>(
					`${this.instanceSelectors.lineQuantity}-${line}`
				)
				if (parsedState.errors) {
					rejectStandardCartMutation(
						standardBoundary,
						new Error(
							typeof parsedState.errors === 'string'
								? parsedState.errors
								: window.cartStrings.error
						)
					)
					quantityElement.value = q.ra(quantityElement, 'value')
					this.updateLiveRegions(line, parsedState.errors)
					this.disableLoading(line)
					return
				}
				await resolveStandardCartMutation(
					standardBoundary,
					parsedState as Cart
				)
				const requiredSections = this.getSectionsToRender()
					.map((section) => section.section)
					.filter((sectionId): sectionId is string =>
						Boolean(sectionId)
					)
				if (!hasRequiredCartSections(parsedState, requiredSections)) {
					try {
						parsedState = await fetchCartWithSections(requiredSections)
					} catch {
						this.showCartError()
						this.disableLoading(line)
						return
					}
				}
				// continue with normal cart update
				const items = document.querySelectorAll(
					this.instanceSelectors.item
				)

				const cartDrawerWrapper = q.os<CartDrawer>('cart-drawer')
				const cartFooter = q.os(this.instanceSelectors.footer)
				if (parsedState.item_count === 0) {
					this.setAttribute(ATTRIBUTES.cartEmpty, '')
					cartFooter &&
					cartFooter.setAttribute(ATTRIBUTES.cartEmpty, '')
					cartDrawerWrapper &&
					cartDrawerWrapper.setAttribute(ATTRIBUTES.cartEmpty, '')
				} else {
					this.removeAttribute(ATTRIBUTES.cartEmpty)
					cartFooter &&
					cartFooter.removeAttribute(ATTRIBUTES.cartEmpty)
					cartDrawerWrapper &&
					cartDrawerWrapper.removeAttribute(ATTRIBUTES.cartEmpty)
				}
				cartDrawerWrapper?.updateItemCount(parsedState as Cart)

				try {
					this.getSectionsToRender().forEach(
						(section: ShopifySectionRenderingSchema) => {
							const sectionEl = q.rs(`#${section.id}`)
							const elementToReplace =
								(section.selector &&
									sectionEl.querySelector(section.selector)) ||
								sectionEl
							if (!section.section)
								throw new Error('no section.section')
							if (!section.selector)
								throw new Error('no section.selector')
							elementToReplace.innerHTML = this.getSectionInnerHTML(
								parsedState.sections[section.section],
								section.selector
							)
						}
					)
				} catch (error) {
					console.error('Error rendering cart sections', error)
					if (this.showCartError()) {
						this.disableLoading(line)
						return
					}
					throw error
				}
				const updatedValue = parsedState.items[parseInt(line) - 1]
					? parsedState.items[parseInt(line) - 1].quantity
					: undefined
				let message = ''
				if (
					items.length === parsedState.items.length &&
					updatedValue !== parseInt(quantityElement.value)
				) {
					if (typeof updatedValue === 'undefined') {
						message = window.cartStrings.error
					} else {
						message = window.cartStrings.quantityError.replace(
							'[quantity]',
							updatedValue
						)
					}
				}
				this.updateLiveRegions(line, message)

				const lineItem = q.os(`${this.instanceSelectors.line}-${line}`)
				if (lineItem?.querySelector(`[name="${name}"]`)) {
					cartDrawerWrapper
						? window.TsDOM.trapFocus(
							cartDrawerWrapper,
							q.rs(`[name="${name}"]`, lineItem)
						)
						: q.rs(`[name="${name}"]`, lineItem).focus()
				} else if (parsedState.item_count === 0 && cartDrawerWrapper) {
					const cartInner = q.rs(
						this.instanceSelectors.cartDrawerInner,
						cartDrawerWrapper
					)
					window.TsDOM.trapFocus(cartInner, cartInner)
				} else if (
					document.querySelector(this.instanceSelectors.item) &&
					cartDrawerWrapper
				) {
					window.TsDOM.trapFocus(
						cartDrawerWrapper,
						q.rs(this.instanceSelectors.itemLink)
					)
				}
				void window.Ucoast.mediaManager.playAllInContainer(this)
				publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' })
				this.disableLoading(line)
			})
			.catch((error) => {
				console.error(error)
				rejectStandardCartMutation(
					standardBoundary,
					error,
					'SERVICE_UNAVAILABLE'
				)
				if (this.showCartError()) {
					this.disableLoading(line)
					return
				}
				this.querySelectorAll(
					this.instanceSelectors.loadingOverlay
				).forEach((overlay) => overlay.classList.add('hidden'))
				this.disableLoading(line)
			})
	}

	updateLiveRegions(line: string, message: string) {
		const lineItemError = q.os(
			`${this.instanceSelectors.lineError}-${line}`
		)
		if (lineItemError) {
			const errorText = q.rs(
				this.instanceSelectors.itemErrorText,
				lineItemError
			)
			errorText.textContent = ''
		}

		this.lineItemStatusElement.setAttribute('aria-hidden', 'true')

		const cartStatus = q.rs(this.instanceSelectors.liveRegionText)
		cartStatus.textContent = message
		cartStatus.setAttribute('aria-hidden', 'false')
		if (message) this.showCartError(message)

		setTimeout(() => {
			cartStatus.setAttribute('aria-hidden', 'true')
		}, 1000)
	}

	getSectionInnerHTML(html: string, selector: string) {
		const newDocument = new DOMParser().parseFromString(html, 'text/html')
		const newElement = q.rs(selector, newDocument.documentElement)
		return newElement.innerHTML
	}

	enableLoading(line: string) {
		const mainCartItems = this.getMainCartItems()
		mainCartItems.setAttribute(CartItems.attributes.disabled, '')

		this.getCartItemElements(line)?.forEach((overlay) =>
			overlay.classList.remove('hidden')
		)

		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur()
		}

		this.lineItemStatusElement.setAttribute('aria-hidden', 'false')
	}

	getMainCartItems() {
		return q.rs(this.instanceSelectors.main)
	}

	getCartItemElements(line: string) {
		return q.ol(
			`${this.instanceSelectors.line}-${line} ${this.instanceSelectors.loadingOverlay}`,
			this
		)
	}

	disableLoading(line: string) {
		this.getMainCartItems().removeAttribute(CartItems.attributes.disabled)
		this.getCartItemElements(line)?.forEach((overlay) =>
			overlay.classList.add('hidden')
		)
	}
}
